export type OauthConfig = {
  clientId: string;
  authorizationEndpoint: string;
  tokenEndpoint: string;
  redirectUri: string;
};

const OAUTH_CONFIG_FIELDS = ['clientId', 'authorizationEndpoint', 'tokenEndpoint', 'redirectUri'] as const;

function isOauthConfig(value: unknown): value is OauthConfig {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Partial<OauthConfig>;
  return OAUTH_CONFIG_FIELDS.every((f) => typeof v[f] === 'string');
}

function isValidOauthConfig(value: OauthConfig): boolean {
  const emptyFields = OAUTH_CONFIG_FIELDS.filter((f) => value[f].length === 0);
  if (emptyFields.length > 0) {
    console.error(
      `config.json: OAUTH_CONFIG has empty values for: ${emptyFields.join(', ')}. ` +
        'If SSO is not configured for this environment, omit OAUTH_CONFIG entirely.'
    );
    return false;
  }
  return true;
}

export const getOauthConfig = async (): Promise<OauthConfig | undefined> => {
  try {
    const response = await fetch('/config.json', { cache: 'no-store' });
    if (!response.ok) {
      throw new Error('Failed to load config.json');
    }
    const config: unknown = await response.json();
    const oauth = (config as { OAUTH_CONFIG?: unknown })?.OAUTH_CONFIG;
    // OAUTH_CONFIG is optional: when absent, the app runs without OAuth
    // and OAuthProvider renders its children directly. Only validate
    // the shape when something IS configured, so a malformed entry
    // surfaces a clear error rather than silently producing a half-
    // configured AuthProvider.
    if (oauth === undefined || oauth === null) {
      return undefined;
    }
    if (!isOauthConfig(oauth)) {
      throw new Error('config.json: OAUTH_CONFIG is malformed (expected clientId, ' + 'authorizationEndpoint, tokenEndpoint, redirectUri as strings)');
    }
    if (!isValidOauthConfig(oauth)) {
      return undefined;
    }
    return oauth;
  } catch (error) {
    console.error('Error loading OAuth config from config.json:', error);
    return undefined;
  }
};
